import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  DatePicker,
  Button,
  Space,
  Progress,
  Table,
  message,
  Typography,
} from 'antd';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import dayjs from 'dayjs';
import { getExpenseStatistics, formatCurrency } from '../../services/expenseService';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

export const ExpenseReportPage: React.FC = () => {
  const [statistics, setStatistics] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().startOf('month'),
    dayjs().endOf('month'),
  ]);

  const loadStatistics = async () => {
    setLoading(true);
    try {
      const data = await getExpenseStatistics({
        start_date: dateRange[0].format('YYYY-MM-DD'),
        end_date: dateRange[1].format('YYYY-MM-DD'),
      });
      setStatistics(data);
    } catch (error) {
      message.error('Không thể tải thống kê');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatistics();
  }, [dateRange]);

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

  const pieData = statistics?.categoryStats?.map((category: any) => ({
    name: category.name,
    value: category.total_amount,
    icon: category.icon,
    color: category.color,
  })) || [];

  const barData = statistics?.monthlyStats?.map((month: any) => ({
    month: month.month,
    amount: month.total_amount,
  })) || [];

  const columns = [
    {
      title: 'Danh mục',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: any) => (
        <Space>
          <span>{record.icon}</span>
          <span>{name}</span>
        </Space>
      ),
    },
    {
      title: 'Số tiền',
      dataIndex: 'total_amount',
      key: 'total_amount',
      render: (amount: number) => formatCurrency(amount),
    },
    {
      title: 'Số giao dịch',
      dataIndex: 'transaction_count',
      key: 'transaction_count',
    },
    {
      title: 'Tỷ lệ',
      key: 'percentage',
      render: (record: any) => {
        const percentage = statistics?.totalExpense > 0 
          ? (record.total_amount / statistics.totalExpense * 100).toFixed(1)
          : '0';
        return (
          <Space>
            <Progress 
              percent={parseFloat(percentage)} 
              size="small" 
              strokeColor={record.color}
            />
            <Text>{percentage}%</Text>
          </Space>
        );
      },
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col span={24}>
            <Title level={3}>Báo cáo Chi tiêu</Title>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <RangePicker
              value={dateRange}
              onChange={(dates) => dates && setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs])}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Button type="primary" onClick={loadStatistics} loading={loading}>
              Tải lại
            </Button>
          </Col>
        </Row>

        {statistics && (
          <>
            <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
              <Col xs={24} sm={12} md={6}>
                <Statistic
                  title="Tổng chi tiêu"
                  value={statistics.totalExpense}
                  formatter={(value) => formatCurrency(value as number)}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Statistic
                  title="Số giao dịch"
                  value={statistics.categoryStats?.reduce((sum: number, cat: any) => sum + cat.transaction_count, 0) || 0}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
              <Col xs={24} md={12}>
                <Card title="Phân bố theo danh mục">
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {pieData.map((entry: any, index: number) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value as number)} />
                    </PieChart>
                  </ResponsiveContainer>
                </Card>
              </Col>
              <Col xs={24} md={12}>
                <Card title="So sánh theo tháng">
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={barData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip formatter={(value) => formatCurrency(value as number)} />
                      <Bar dataKey="amount" fill="#1890ff" />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Card title="Chi tiết theo danh mục">
                  <Table
                    columns={columns}
                    dataSource={statistics.categoryStats}
                    rowKey="id"
                    pagination={false}
                  />
                </Card>
              </Col>
            </Row>
          </>
        )}
      </Card>
    </div>
  );
};